import { Typography } from '@euler/app/components/typography';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { usePatchedAnimatedTextInputRef } from '@euler/functions/usePatchedAnimatedTextInputRef.web';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import { useSharedValueFrom } from '@euler/utils/hooks';
import addMonths from 'date-fns/addMonths';
import differenceInMonths from 'date-fns/differenceInMonths';
import getMonth from 'date-fns/getMonth';
import { LinearGradient } from 'expo-linear-gradient';
import { MotiView } from 'moti';
import { range } from 'ramda';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  LayoutRectangle,
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  View,
  ViewStyle,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  SharedValue,
  runOnJS,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useVector } from 'react-native-redash';
import { Subject } from 'rxjs';

import {
  MaintenancePlanStartParams,
  MaintenancePlanValueParams,
} from './types';

const kThumbWidthFactor = 2.1;

const AnimatedTextInput = Animated.createAnimatedComponent(TextInput);

type Props = {
  name: string;
  translateX$: Subject<number>;
  selectedValue?: MaintenancePlanValueParams;
  isConfirmed?: boolean;
  hasConstructionJob?: boolean;
  constructionJobStatus?: InspectionTaskJobStatus;
  index: number;
  style?: StyleProp<ViewStyle>;
  onChange?: (params: MaintenancePlanValueParams) => void;
} & MaintenancePlanStartParams;

export const MaintenancePlanSlider = memo(({ style, ...props }: Props) => {
  const { rpx } = useResponsiveContext();
  const [layout, setLayout] = useState<LayoutRectangle>();
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setLayout(e.nativeEvent.layout);
  }, []);
  return (
    <View
      css={`
        height: ${rpx(142)}px;
      `}
      style={style}
    >
      <View style={StyleSheet.absoluteFill} onLayout={onLayout} />
      {layout != null && (
        <TrackView width={layout.width} height={layout.height} {...props} />
      )}
    </View>
  );
});

const TrackView = memo(
  ({
    name,
    translateX$,
    width,
    height,
    startDate,
    currentVehicleMileage,
    currentVehicleAge,
    style,
    index,
    isConfirmed,
    hasConstructionJob,
    constructionJobStatus,
    selectedValue,
    onChange,
  }: Props & { width: number; height: number }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const { tickColor, tickTextColor, trackBgColor } =
      theme.task.maintenancePlan.slider;
    const unitWidth = (width - rpx(40)) / 15; /** 15 months */
    const trackWidth = width;
    const thumbWidth = unitWidth * kThumbWidthFactor;
    const initialTranslateX = useMemo(() => {
      if (selectedValue == null) return 0;
      const offset = Math.min(
        differenceInMonths(selectedValue.date, startDate),
        15,
      );
      return Math.min(trackWidth - thumbWidth, Math.max(0, offset * unitWidth));
    }, [selectedValue, startDate, thumbWidth, trackWidth, unitWidth]);

    const showConfirmedThumbView =
      hasConstructionJob &&
      (selectedValue == null ||
        currentVehicleMileage === selectedValue.mileage);

    const translateX = useSharedValue(initialTranslateX);
    const normalThumbContentViewOpacity = useSharedValue(
      showConfirmedThumbView ? 0 : 1,
    );

    const tapStart = useSharedValue(0);
    const tapStartPoint = useVector(0, 0);

    const end = useCallback(
      (offset: number) => {
        const avgMileagePerYear = currentVehicleMileage / currentVehicleAge;
        const value = valueAt(
          getMonth(startDate) + 1,
          currentVehicleMileage,
          avgMileagePerYear,
          offset * unitWidth,
          unitWidth,
        );
        const date = addMonths(startDate, offset);
        const mileage = Math.round(
          currentVehicleMileage + avgMileagePerYear * (offset / 12),
        );
        if (hasConstructionJob && currentVehicleMileage === mileage) {
          normalThumbContentViewOpacity.value = 0;
        } else {
          normalThumbContentViewOpacity.value = 1;
        }
        onChange?.({ mileage: value.mileage, date });
      },
      [
        currentVehicleAge,
        currentVehicleMileage,
        hasConstructionJob,
        normalThumbContentViewOpacity,
        onChange,
        startDate,
        unitWidth,
      ],
    );

    const tap = Gesture.Tap()
      .onBegin(e => {
        tapStartPoint.x.value = e.absoluteX;
        tapStartPoint.y.value = e.absoluteY;
        tapStart.value = Date.now();
      })
      .onEnd(e => {
        const diff = Date.now() - tapStart.value;
        if (
          diff < 350 &&
          Math.abs(e.absoluteX - tapStartPoint.x.value) < 10 &&
          Math.abs(e.absoluteY - tapStartPoint.y.value) < 10
        ) {
          const offset = Math.min(
            13,
            Math.max(0, Math.round(e.x / unitWidth) - 1),
          );
          const translationX =
            offset >= 13
              ? trackWidth - thumbWidth
              : Math.min(
                  trackWidth - thumbWidth,
                  Math.max(0, offset * unitWidth),
                );
          translateX.value = withTiming(
            translationX,
            { duration: 250, easing: Easing.inOut(Easing.linear) },
            () => {
              runOnJS(end)(offset);
            },
          );
        }
      });

    useEffect(() => {
      const sub = translateX$.subscribe(value => {
        translateX.value = withTiming(value, {
          duration: 200,
          easing: Easing.inOut(Easing.linear),
        });
      });
      return () => sub.unsubscribe();
    }, [translateX, translateX$]);

    useEffect(() => {
      normalThumbContentViewOpacity.value = showConfirmedThumbView ? 0 : 1;
    }, [normalThumbContentViewOpacity, showConfirmedThumbView]);

    return (
      <MotiView
        from={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ type: 'timing', duration: 750 }}
        delay={Math.min(1200, index * 50)}
        css={`
          background-color: ${trackBgColor};
          height: ${height}px;
          border-radius: ${height / 2}px;
        `}
        style={style}
      >
        <GestureDetector gesture={tap}>
          <Animated.View
            css={`
              flex: 1;
              flex-direction: row;
              justify-content: flex-start;
              align-items: stretch;
            `}
          >
            {range(0, 7).map(k => (
              <View
                key={k}
                css={`
                  width: ${unitWidth * 2}px;
                  justify-content: center;
                  flex-grow: 0;
                  flex-shrink: 0;
                `}
              >
                <View
                  css={`
                    border-right-width: 1px;
                    border-right-color: ${tickColor};
                  `}
                >
                  <Typography.Label
                    regular
                    size={rpx(24)}
                    lineHeight={rpx(36)}
                    color={tickTextColor}
                    css={`
                      text-align: center;
                    `}
                  >
                    {`${getMonth(addMonths(startDate, k * 2)) + 1}月`}
                  </Typography.Label>
                </View>
              </View>
            ))}
            <View
              css={`
                flex: 1;
                justify-content: center;
                align-items: center;
              `}
            >
              <Icon name="clock" size={rpx(24)} color={tickTextColor} />
            </View>
          </Animated.View>
        </GestureDetector>
        {hasConstructionJob && (
          <ConfirmedImmediatePlanThumbView
            date={startDate}
            mileage={currentVehicleMileage}
            width={thumbWidth}
            height={height}
            constructionJobStatus={constructionJobStatus}
          />
        )}
        <ThumbView
          name={name}
          startDate={startDate}
          currentVehicleAge={currentVehicleAge}
          currentVehicleMileage={currentVehicleMileage}
          trackWidth={width}
          width={thumbWidth}
          height={height}
          unitWidth={unitWidth}
          translateX={translateX}
          normalThumbContentViewOpacity={normalThumbContentViewOpacity}
          isConfirmed={isConfirmed}
          hasConstructionJob={hasConstructionJob}
          constructionJobStatus={constructionJobStatus}
          selectedValue={selectedValue}
          onChange={onChange}
        />
      </MotiView>
    );
  },
);

export const ThumbView = memo(
  ({
    translateX,
    normalThumbContentViewOpacity,
    unitWidth,
    trackWidth,
    startDate,
    currentVehicleAge,
    currentVehicleMileage,
    selectedValue,
    isConfirmed,
    hasConstructionJob,
    constructionJobStatus,
    width,
    height,
    style,
    onChange,
  }: {
    name: string;
    translateX: SharedValue<number>;
    normalThumbContentViewOpacity: SharedValue<number>;
    startDate: Date;
    currentVehicleMileage: number;
    currentVehicleAge: number;
    selectedValue?: MaintenancePlanValueParams;
    isConfirmed?: boolean;
    hasConstructionJob?: boolean;
    constructionJobStatus?: InspectionTaskJobStatus;
    unitWidth: number;
    trackWidth: number;
    width: number;
    height: number;
    style?: StyleProp<ViewStyle>;
    onChange?: (params: MaintenancePlanValueParams) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const {
      thumbStartColor,
      thumbStopColor,
      thumbMileageTextColor,
      thumbStartColorPending,
      thumbStopColorPending,
      thumbStartColorConfirmed,
      thumbStopColorConfirmed,
    } = theme.task.maintenancePlan.slider;

    const startTranslateX = useSharedValue(0);
    const scale = useSharedValue(1);
    const avgMileagePerYear = currentVehicleMileage / currentVehicleAge;
    const startMonth = getMonth(startDate) + 1;
    const isPanActive = useSharedValue(false);

    const endWith = useCallback(
      (translationX: number) => {
        const { mileage, offset } = valueAt(
          startMonth,
          currentVehicleMileage,
          avgMileagePerYear,
          translationX,
          unitWidth,
        );
        const date = addMonths(startDate, offset);
        if (hasConstructionJob && currentVehicleMileage === mileage) {
          normalThumbContentViewOpacity.value = 0;
        } else {
          normalThumbContentViewOpacity.value = 1;
        }
        onChange?.({ mileage, date });
      },
      [
        avgMileagePerYear,
        currentVehicleMileage,
        hasConstructionJob,
        normalThumbContentViewOpacity,
        onChange,
        startDate,
        startMonth,
        unitWidth,
      ],
    );
    const gesture = Gesture.Pan()
      .onBegin(() => {
        scale.value = 1.2;
        startTranslateX.value = translateX.value;
        normalThumbContentViewOpacity.value = 1;
        isPanActive.value = true;
      })
      .onUpdate(e => {
        translateX.value = Math.min(
          trackWidth - width,
          Math.max(0, startTranslateX.value + e.translationX),
        );
      })
      .onEnd(e => {
        scale.value = 1;
        let translationX = Math.min(
          trackWidth - width,
          Math.max(0, startTranslateX.value + e.translationX),
        );
        const value = valueAt(
          startMonth,
          currentVehicleMileage,
          avgMileagePerYear,
          translationX,
          unitWidth,
        );
        if (value.offset === 0) {
          translationX = 0;
        }
        translateX.value =
          value.offset === 0
            ? withTiming(0, {
                duration: 250,
                easing: Easing.out(Easing.linear),
              })
            : translationX;
        runOnJS(endWith)(translationX);
      })
      .onFinalize((_, res) => {
        scale.value = 1;
        isPanActive.value = false;
        if (!res) {
          runOnJS(endWith)(translateX.value);
        }
      });

    const animatedStyle = useAnimatedStyle(() => {
      return {
        transform: [
          {
            translateX: translateX.value,
          },
          {
            scale: withTiming(scale.value, {
              duration: 150,
              easing: Easing.inOut(Easing.linear),
            }),
          },
        ],
      };
    });

    const mileageTextRef = usePatchedAnimatedTextInputRef();
    const [currentText] = useSharedValueFrom(
      isConfirmed && selectedValue != null
        ? thumbTextByValue(selectedValue.mileage, selectedValue.date)
        : undefined,
    );

    // $hack(eric): shared value dependency to trigger initial animated value update.
    const __hack = useSharedValue('');

    const mileageTextProps = useAnimatedProps(() => {
      if (currentText.value != null && !isPanActive.value) {
        return {
          text: currentText.value + __hack.value,
        } as any;
      }
      const value = valueAt(
        startMonth,
        currentVehicleMileage,
        avgMileagePerYear,
        translateX.value,
        unitWidth,
      );
      return {
        text: value.text + __hack.value,
      } as any;
    });

    const startColor = isConfirmed
      ? thumbStartColorConfirmed
      : selectedValue != null
      ? thumbStartColorPending
      : thumbStartColor;
    const stopColor = isConfirmed
      ? thumbStopColorConfirmed
      : selectedValue != null
      ? thumbStopColorPending
      : thumbStopColor;

    const normalThumbContentViewStyle = useAnimatedStyle(() => {
      return {
        opacity: normalThumbContentViewOpacity.value,
      };
    });

    const confirmedThumbContentViewStyle = useAnimatedStyle(() => {
      return {
        opacity: 1 - normalThumbContentViewOpacity.value,
      };
    });

    useEffect(() => {
      // append invisible whitespace to trigger animated value update
      // otherwise, unless the thumb is dragged the text will not be rendered.
      __hack.value = ' ';
    }, [__hack]);

    return (
      <GestureDetector gesture={gesture}>
        <Animated.View
          css={`
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: ${width}px;
            height: ${height}px;
          `}
          style={[style, animatedStyle]}
        >
          <Animated.View
            style={[StyleSheet.absoluteFill, normalThumbContentViewStyle]}
          >
            <LinearGradient
              css={`
                flex: 1;
                border-radius: ${rpx(40)}px;
                justify-content: center;
                align-items: center;
              `}
              pointerEvents="none"
              colors={[startColor, stopColor]}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
            >
              <AnimatedTextInput
                ref={mileageTextRef}
                editable={false}
                showSoftInputOnFocus={false}
                pointerEvents={'none'}
                animatedProps={mileageTextProps}
                multiline
                numberOfLines={2}
                css={`
                  font-family: ${FontFamily.NotoSans.Bold};
                  font-size: ${rpx(24)}px;
                  line-height: ${rpx(40)}px;
                  font-weight: 900;
                  color: ${thumbMileageTextColor};
                  padding: 0;
                `}
                style={
                  Platform.OS === 'web'
                    ? {
                        // hack: on web, the text input here will be rendered as a
                        // textarea element that overflows the container.
                        // todo(eric): find out the root cause of this issue.
                        textAlign: 'center',
                        width: '100%',
                      }
                    : undefined
                }
              />
            </LinearGradient>
          </Animated.View>
          <Animated.View
            style={[StyleSheet.absoluteFill, confirmedThumbContentViewStyle]}
          >
            <LinearGradient
              style={StyleSheet.absoluteFill}
              css={`
                border-radius: ${rpx(40)}px;
                justify-content: center;
                align-items: center;
              `}
              pointerEvents="none"
              colors={[thumbStartColorConfirmed, thumbStopColorConfirmed]}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
            >
              <StaticThumbViewContent
                date={startDate}
                mileage={currentVehicleMileage}
                constructionJobStatus={constructionJobStatus}
              />
            </LinearGradient>
          </Animated.View>
        </Animated.View>
      </GestureDetector>
    );
  },
);

const StaticThumbViewContent = memo(
  ({
    mileage,
    date,
    constructionJobStatus,
  }: {
    mileage: number;
    date: Date;
    constructionJobStatus?: InspectionTaskJobStatus;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      task: {
        maintenancePlan: {
          slider: { thumbMileageTextColor, thumbStatusTextColor },
        },
      },
    } = usePresentationTheme();
    const [year, month] = [date.getFullYear(), getMonth(date) + 1];
    const statusText =
      constructionJobStatus != null
        ? constructionJobStatus === InspectionTaskJobStatus.Pending
          ? '计划施工'
          : constructionJobStatus === InspectionTaskJobStatus.InProgress
          ? '正在施工'
          : '施工完成'
        : undefined;
    const timeText = [
      year === new Date().getFullYear() ? `${month}月` : `明年${month}月`,
    ].join('\n');
    return (
      <View>
        <View>
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Bold};
              font-size: ${rpx(24)}px;
              line-height: ${rpx(40)}px;
              font-weight: 900;
              color: ${thumbMileageTextColor};
            `}
          >{`${mileage}KM`}</Text>
        </View>
        <HStack justifyContent="flex-start">
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Bold};
              font-size: ${rpx(24)}px;
              line-height: ${rpx(40)}px;
              font-weight: 900;
              color: ${thumbMileageTextColor};
            `}
          >
            {timeText}
          </Text>
          {statusText != null && (
            <Text
              numberOfLines={1}
              css={`
                font-family: ${FontFamily.NotoSans.Bold};
                font-size: ${rpx(18)}px;
                line-height: ${rpx(40)}px;
                font-weight: 900;
                color: ${thumbStatusTextColor};
              `}
            >
              {` (${statusText})`}
            </Text>
          )}
        </HStack>
      </View>
    );
  },
);

export const ConfirmedImmediatePlanThumbView = memo(
  ({
    width,
    height,
    style,
    ...props
  }: {
    mileage: number;
    date: Date;
    width: number;
    height: number;
    constructionJobStatus?: InspectionTaskJobStatus;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      task: {
        maintenancePlan: {
          slider: { thumbStartColorConfirmed, thumbStopColorConfirmed },
        },
      },
    } = usePresentationTheme();
    return (
      <View
        css={`
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: ${width}px;
          height: ${height}px;
        `}
        style={style}
      >
        <LinearGradient
          css={`
            flex: 1;
            border-radius: ${rpx(40)}px;
            justify-content: center;
            align-items: center;
          `}
          pointerEvents="none"
          colors={[thumbStartColorConfirmed, thumbStopColorConfirmed]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
        >
          <StaticThumbViewContent {...props} />
        </LinearGradient>
      </View>
    );
  },
);

function valueAt(
  startMonth: number,
  currentVehicleMileage: number,
  avgMileagePerYear: number,
  translateX: number,
  unitWidth: number,
) {
  'worklet';
  const offset = Math.floor(
    (translateX + unitWidth * (kThumbWidthFactor - 2)) / unitWidth,
  );
  const mileage =
    currentVehicleMileage + Math.round(avgMileagePerYear * (offset / 12));
  const m = startMonth + offset;
  const month = m <= 12 ? m : m - 12;
  const isNextYear = m > 12;
  const text = [`${mileage}KM`, `${isNextYear ? '明年' : ''}${month}月`].join(
    '\n',
  );
  return { mileage, month, isNextYear, text, offset };
}

function thumbTextByValue(mileage: number, date: Date) {
  'worklet';
  const currentYear = new Date().getFullYear();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  let text = `${mileage}KM\n`;
  if (year === currentYear) {
    text += `${month}月`;
  } else if (year === currentYear + 1) {
    text += `明年${month}月`;
  } else {
    text += `${year}年${month}月`;
  }
  return text;
}
